import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../api/axios';
import { MapPin, ArrowLeft, Download, Loader2, Calendar, FileText } from 'lucide-react';

// Cores dos postos
const POSTO_COLORS = {
  'POSTO JP&HG LOBO': { bg: '#10B981', gradient: 'from-emerald-500 to-emerald-700' },
  'POSTO CARAUARI': { bg: '#EF4444', gradient: 'from-red-500 to-red-700' },
  'REDE BONS AMIGOS': { bg: '#3B82F6', gradient: 'from-blue-500 to-blue-700' },
  'PONTÃO BARROTE': { bg: '#6B7280', gradient: 'from-gray-500 to-gray-700' },
};
const DEFAULT_GRADIENT = 'from-slate-600 to-slate-800';

export default function GerarRequisicaoPage() {
  const [postos, setPostos] = useState([]);
  const [selectedPosto, setSelectedPosto] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/postos');
        setPostos(res.data || []);
      } catch {
        toast.error('Erro ao carregar postos.');
      }
    };
    load();
    // Default: current month
    const now = new Date();
    const first = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const lastStr = `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
    setStartDate(first);
    setEndDate(lastStr);
  }, []);

  const handleGerarRequisicao = async () => {
    if (!startDate || !endDate) {
      toast.error('Selecione o periodo.');
      return;
    }
    setLoading(true);
    try {
      const res = await api.get('/reports/requisicao-pdf', {
        params: { posto_id: selectedPosto.id, start_date: startDate, end_date: endDate },
        responseType: 'blob',
        timeout: 120000
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `requisicao_${selectedPosto.name?.replace(/\s/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Requisicao gerada com sucesso!');
    } catch {
      toast.error('Erro ao gerar requisicao. Verifique se existem abastecimentos no periodo.');
    } finally {
      setLoading(false);
    }
  };

  // VIEW: Postos list
  if (!selectedPosto) {
    return (
      <div className="space-y-6" data-testid="gerar-requisicao-page">
        <div>
          <h1 className="text-2xl font-bold text-[#003366]">Gerar Requisicao</h1>
          <p className="text-sm text-gray-500 mt-1">Selecione um posto para gerar a requisicao de abastecimento</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {postos.map(posto => {
            const colors = POSTO_COLORS[posto.name] || {};
            const gradient = colors.gradient || DEFAULT_GRADIENT;
            return (
              <Card
                key={posto.id}
                className="cursor-pointer hover:shadow-xl transition-all group overflow-hidden border-0"
                onClick={() => setSelectedPosto(posto)}
                data-testid={`posto-card-${posto.id}`}
              >
                <div className={`bg-gradient-to-br ${gradient} p-6 text-white`}>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                      <MapPin size={28} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-lg uppercase">{posto.name}</p>
                      <p className="text-white/70 text-sm mt-0.5">{posto.address || 'Clique para gerar requisicao'}</p>
                    </div>
                    <FileText size={24} className="text-white/40 group-hover:text-white/80 transition-colors" />
                  </div>
                </div>
              </Card>
            );
          })}
          {postos.length === 0 && (
            <p className="text-gray-400 text-sm col-span-full text-center py-8">Nenhum posto cadastrado.</p>
          )}
        </div>
      </div>
    );
  }

  // VIEW: Date selection + Generate
  const postoColors = POSTO_COLORS[selectedPosto.name] || {};
  const postoGradient = postoColors.gradient || DEFAULT_GRADIENT;

  return (
    <div className="space-y-6" data-testid="gerar-requisicao-form">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => setSelectedPosto(null)} data-testid="back-to-postos">
          <ArrowLeft size={18} className="mr-1" /> Voltar
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-[#003366]">Gerar Requisicao</h1>
          <p className="text-sm text-gray-500">{selectedPosto.name?.toUpperCase()}</p>
        </div>
      </div>

      {/* Posto info card */}
      <div className={`bg-gradient-to-br ${postoGradient} rounded-xl p-6 text-white`}>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center">
            <MapPin size={32} />
          </div>
          <div>
            <p className="font-bold text-xl uppercase">{selectedPosto.name}</p>
            <p className="text-white/70 text-sm">{selectedPosto.address || ''}</p>
          </div>
        </div>
      </div>

      {/* Date selection */}
      <Card>
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <Calendar size={20} className="text-[#003366]" />
            <h2 className="text-lg font-semibold text-[#003366]">Selecione o Periodo</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-medium">Data Inicial</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="requisicao-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-medium">Data Final</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="requisicao-end-date"
              />
            </div>
          </div>

          <Button
            onClick={handleGerarRequisicao}
            disabled={loading || !startDate || !endDate}
            className="w-full bg-[#003366] hover:bg-[#002244] text-white h-12 text-base"
            data-testid="gerar-requisicao-btn"
          >
            {loading ? (
              <><Loader2 className="animate-spin mr-2" size={20} /> Gerando Requisicao...</>
            ) : (
              <><Download size={20} className="mr-2" /> Gerar Requisicao</>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
